import { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import QRCode from "react-qr-code";
import { Copy, MessageCircle, ArrowLeft, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import Navbar, { MobileBottomBar } from "@/components/Navbar";
import { Footer } from "@/components/Contact";
import { api, imgSrc, formatApiError } from "@/lib/api";
import { track, whatsappLink } from "@/lib/analytics";
import { useContent } from "@/App";

const inputCls = "mt-1 w-full rounded-lg border border-white/10 bg-charcoal-800 px-4 py-3 text-sm text-cream focus:border-copper focus:outline-none [color-scheme:dark]";
const labelCls = "block text-[10px] font-mono uppercase tracking-[0.2em] text-copper-400";

function fmtINR(n) {
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

export default function Book() {
  const content = useContent();
  const [params] = useSearchParams();
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    name: "", phone: "", email: "", notes: "",
    checkin: params.get("checkin") || "",
    checkout: params.get("checkout") || "",
    guests: Number(params.get("guests")) || 2,
    unit: "entire-villa",
  });
  const [blocked, setBlocked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const settings = content?.settings || {};
  const rooms = content?.rooms || [];

  useEffect(() => {
    api.get("/availability").then((r) => setBlocked(r.data.blocked || [])).catch(() => {});
  }, []);

  const conflict = useMemo(() => {
    if (!form.checkin || !form.checkout) return null;
    const ci = new Date(form.checkin), co = new Date(form.checkout);
    return blocked.find((r) => ci < new Date(r.to) && new Date(r.from) < co) || null;
  }, [form.checkin, form.checkout, blocked]);

  const nights = useMemo(() => {
    if (!form.checkin || !form.checkout) return 0;
    const n = Math.round((new Date(form.checkout) - new Date(form.checkin)) / 86400000);
    return n > 0 ? n : 0;
  }, [form.checkin, form.checkout]);

  const [quote, setQuote] = useState(null);
  useEffect(() => {
    if (!form.checkin || !form.checkout || nights === 0) { setQuote(null); return; }
    api.get(`/bookings/quote?checkin=${form.checkin}&checkout=${form.checkout}&guests=${form.guests}`)
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null));
  }, [form.checkin, form.checkout, form.guests, nights]);
  const total = quote?.total ?? null;
  const advance = quote?.advance ?? null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    track("booking_submitted", { checkin: form.checkin, checkout: form.checkout, guests: form.guests });
    try {
      const { data } = await api.post("/bookings", { ...form, guests: Number(form.guests) });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const ref = result ? result.id.slice(0, 8).toUpperCase() : "";
  const pay = result?.payment || {};
  const upiLink = pay.upi_id
    ? `upi://pay?pa=${pay.upi_id}&pn=${encodeURIComponent(pay.upi_name || "Mystique Copper Villa")}${result?.amount_advance ? `&am=${result.amount_advance}` : ""}&cu=INR&tn=${encodeURIComponent(`MCV-${ref}`)}`
    : "";
  const waConfirm = pay.whatsapp_number
    ? whatsappLink(pay.whatsapp_number, `Hi! I just placed booking request MCV-${ref} (${form.checkin} → ${form.checkout}, ${form.guests} guests, ${form.name}). Sharing my payment screenshot here.`)
    : null;

  return (
    <main className="min-h-screen" data-testid="booking-page">
      <SEO title="Book Your Stay — Mystique Copper Villa, Karwar" description="Reserve the entire villa direct. Pay the advance securely via UPI or GPay." path="/book" />
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 pb-24 pt-32 sm:px-8 lg:pt-40">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-copper-300 hover:text-copper-400" data-testid="book-back-link">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>

        {!result ? (
          <>
            <h1 className="mt-6 font-serif text-4xl tracking-tight text-cream sm:text-5xl">Reserve your <span className="italic text-copper-gradient">dates.</span></h1>
            <p className="mt-3 max-w-xl text-base font-light text-cream-muted">Send your booking request and pay the advance via UPI / GPay. We confirm on WhatsApp within a few hours.</p>

            {conflict && (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-red-400/40 bg-red-400/10 p-4 text-sm text-cream" data-testid="booking-conflict-warning" role="alert">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                These dates overlap an existing booking{conflict.note ? ` (${conflict.note})` : ""}. Please choose different dates.
              </div>
            )}

            <form onSubmit={submit} className="mt-10 grid gap-10 lg:grid-cols-[1fr_360px]">
              <div className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block"><span className={labelCls}>Check-in</span>
                    <input type="date" required min={today} value={form.checkin} onChange={set("checkin")} data-testid="book-checkin-input" className={inputCls} /></label>
                  <label className="block"><span className={labelCls}>Check-out</span>
                    <input type="date" required min={form.checkin || today} value={form.checkout} onChange={set("checkout")} data-testid="book-checkout-input" className={inputCls} /></label>
                  <label className="block"><span className={labelCls}>Guests</span>
                    <select value={form.guests} onChange={set("guests")} data-testid="book-guests-select" className={inputCls}>
                      {[...Array(8)].map((_, i) => <option key={i + 1} value={i + 1} className="bg-charcoal-800">{i + 1} {i === 0 ? "guest" : "guests"}</option>)}
                    </select></label>
                  <label className="block"><span className={labelCls}>Unit</span>
                    <select value={form.unit} onChange={set("unit")} data-testid="book-unit-select" className={inputCls}>
                      {rooms.length ? rooms.map((r) => <option key={r.id} value={r.id} className="bg-charcoal-800">{r.name}</option>) : <option value="entire-villa">The Entire Villa</option>}
                    </select></label>
                  <label className="block"><span className={labelCls}>Full name</span>
                    <input required value={form.name} onChange={set("name")} data-testid="book-name-input" className={inputCls} placeholder="Your name" /></label>
                  <label className="block"><span className={labelCls}>Phone / WhatsApp</span>
                    <input required type="tel" value={form.phone} onChange={set("phone")} data-testid="book-phone-input" className={inputCls} placeholder="+91 …" /></label>
                </div>
                <label className="block"><span className={labelCls}>Email (optional)</span>
                  <input type="email" value={form.email} onChange={set("email")} data-testid="book-email-input" className={inputCls} placeholder="you@example.com" /></label>
                <label className="block"><span className={labelCls}>Notes (optional)</span>
                  <textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="book-notes-input" className={inputCls} placeholder="Occasion, arrival time, anything we should know…" /></label>
                <button type="submit" disabled={busy || !!conflict || nights === 0} data-testid="book-submit-btn"
                  className="w-full rounded-full bg-copper py-4 text-xs font-mono uppercase tracking-[0.2em] text-charcoal transition-all duration-300 hover:bg-copper-300 hover:shadow-[0_0_30px_rgba(200,125,85,0.4)] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-12">
                  {busy ? "Reserving…" : "Reserve & Get Payment QR"}
                </button>
              </div>

              <aside className="h-fit rounded-2xl border border-copper/25 bg-charcoal-800 p-6" data-testid="booking-summary">
                {content?.hero?.image && <img src={imgSrc(content.hero.image)} alt="Mystique Copper Villa" className="aspect-[16/9] w-full rounded-xl object-cover" />}
                <h2 className="mt-4 font-serif text-xl text-cream">Your stay</h2>
                <dl className="mt-3 space-y-2 text-sm font-light text-cream-muted">
                  <div className="flex justify-between"><dt>Dates</dt><dd className="text-cream">{form.checkin || "—"} → {form.checkout || "—"}</dd></div>
                  <div className="flex justify-between"><dt>Nights</dt><dd className="text-cream">{nights || "—"}</dd></div>
                  <div className="flex justify-between"><dt>Guests</dt><dd className="text-cream">{form.guests}</dd></div>
                  {total !== null && (
                    <>
                      <div className="flex justify-between border-t border-white/10 pt-2"><dt>Total</dt><dd className="text-cream">{fmtINR(total)}</dd></div>
                      <div className="flex justify-between"><dt>Advance to pay now</dt><dd className="text-copper-300">{fmtINR(advance)}</dd></div>
                      {quote?.price_note && <div className="flex justify-between text-xs"><dt className="text-cream-dim">Rate</dt><dd className="text-right text-cream-dim">{quote.price_note}</dd></div>}
                    </>
                  )}
                </dl>
                {total === null && <p className="mt-3 text-xs italic text-cream-dim">Exact tariff will be confirmed on WhatsApp after your request.</p>}
              </aside>
            </form>
          </>
        ) : (
          <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_360px]" data-testid="booking-success">
            <div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-copper-400" />
                <h1 className="font-serif text-3xl text-cream sm:text-4xl">Dates reserved, {form.name.split(" ")[0]}.</h1>
              </div>
              <p className="mt-4 max-w-lg text-base font-light leading-relaxed text-cream-muted">
                Booking reference <span className="font-mono text-copper-300">MCV-{ref}</span> · {form.checkin} → {form.checkout} · {form.guests} guests.
                Your dates are held as <span className="text-cream">pending</span>. Pay the advance below and WhatsApp us the screenshot — we confirm within a few hours.
              </p>
              {pay.payment_note && <p className="mt-4 rounded-xl border border-copper/25 bg-copper/10 p-4 text-sm font-light text-cream-muted">{pay.payment_note}</p>}
              {pay.bank_details && (
                <div className="mt-6 rounded-xl border border-white/10 bg-charcoal-800 p-5">
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-copper-400">Bank transfer details</p>
                  <pre className="mt-2 whitespace-pre-wrap text-sm font-light text-cream-muted">{pay.bank_details}</pre>
                </div>
              )}
              {waConfirm && (
                <a href={waConfirm} target="_blank" rel="noreferrer" onClick={() => track("payment_whatsapp_click", { ref })}
                  data-testid="whatsapp-confirm-btn"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-copper px-8 py-4 text-xs font-mono uppercase tracking-[0.18em] text-charcoal transition-colors hover:bg-copper-300">
                  <MessageCircle className="h-4 w-4" /> Send Payment Screenshot on WhatsApp
                </a>
              )}
            </div>
            <aside className="h-fit rounded-2xl border border-copper/30 bg-charcoal-800 p-8 text-center">
              {result.amount_advance && <p className="font-serif text-3xl text-copper-gradient">{fmtINR(result.amount_advance)}</p>}
              <p className="mt-1 text-xs font-mono uppercase tracking-[0.2em] text-cream-muted">Advance · UPI / GPay</p>
              {upiLink ? (
                <>
                  <div className="mx-auto mt-6 w-fit rounded-xl bg-charcoal p-4" data-testid="upi-qr-code">
                    <QRCode value={upiLink} size={180} fgColor="#F7F4EF" bgColor="#121214" />
                  </div>
                  <p className="mt-4 text-xs font-light text-cream-dim">Scan with GPay, PhonePe or any UPI app</p>
                  <button onClick={() => { navigator.clipboard.writeText(pay.upi_id); toast.success("UPI ID copied"); }}
                    data-testid="copy-upi-btn"
                    className="mt-4 inline-flex items-center gap-2 rounded-full border border-copper/40 px-4 py-2 font-mono text-xs text-copper-300 hover:bg-copper/10">
                    <Copy className="h-3.5 w-3.5" /> {pay.upi_id}
                  </button>
                </>
              ) : (
                <p className="mt-6 text-sm font-light text-cream-muted">Payment details will be shared with you on WhatsApp.</p>
              )}
            </aside>
          </div>
        )}
      </div>
      <Footer settings={settings} />
      <MobileBottomBar />
    </main>
  );
}
